import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronUp, ChevronsUpDown, ChevronRight, Package, Truck, Warehouse, Users, ArrowRight } from "lucide-react";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";

// ------- Status → semantic bucket, color, and progress on the lifecycle ring -------
// Blue = pending, Green = satisfied, Red = trouble
const STATUS_META = {
  draft:      { bucket: "pending",   label: "Ordered",     progress: 0.15 },
  ordered:    { bucket: "pending",   label: "Ordered",     progress: 0.15 },
  confirmed:  { bucket: "pending",   label: "In Transit",  progress: 0.35 },
  shipped:    { bucket: "pending",   label: "Shipped",     progress: 0.45 },
  received:   { bucket: "satisfied", label: "Received",    progress: 0.60 },
  paid:       { bucket: "pending",   label: "Processing",  progress: 0.35 },
  processing: { bucket: "pending",   label: "Processing",  progress: 0.35 },
  "awaiting fulfillment": { bucket: "pending", label: "Awaiting Fulfillment", progress: 0.35 },
  fulfilled:  { bucket: "satisfied", label: "Fulfilled",   progress: 1.00 },
  sold:       { bucket: "satisfied", label: "Sold",        progress: 1.00 },
  promo:      { bucket: "satisfied", label: "Promo",       progress: 1.00 },
  cancelled:  { bucket: "trouble",   label: "Cancelled",   progress: 0.50 },
  rma:        { bucket: "trouble",   label: "RMA",         progress: 0.70 },
  lost:       { bucket: "trouble",   label: "Lost",        progress: 0.60 },
  stolen:     { bucket: "trouble",   label: "Stolen",      progress: 0.60 },
  damaged:    { bucket: "trouble",   label: "Damaged",     progress: 0.60 },
};

const BUCKET_COLOR = {
  pending:   { arc: "#2563eb", soft: "#dbeafe", text: "#1d4ed8", pill: "bg-blue-100 text-blue-800 border-blue-200" },
  satisfied: { arc: "#059669", soft: "#d1fae5", text: "#047857", pill: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  trouble:   { arc: "#dc2626", soft: "#fee2e2", text: "#b91c1c", pill: "bg-red-100 text-red-800 border-red-200" },
};

const metaFor = (status) => STATUS_META[(status || "").toLowerCase()] || { bucket: "pending", label: status || "—", progress: 0.15 };

// ------- Thick circular arrow (donut arc + arrowhead) -------
const CircularArrow = ({ status, size = 220 }) => {
  const m = metaFor(status);
  const color = BUCKET_COLOR[m.bucket];
  const r = 88;
  const cx = size / 2, cy = size / 2;
  const stroke = 22;
  const circ = 2 * Math.PI * r;
  const arcLen = circ * m.progress;
  const endAngleDeg = 360 * m.progress - 90; // top-start rotation applied via SVG transform
  const rad = (endAngleDeg * Math.PI) / 180;
  const tipX = cx + r * Math.cos(rad);
  const tipY = cy + r * Math.sin(rad);
  const tangent = endAngleDeg + 90;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="lifecycle-ring">
        {/* soft base track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color.soft} strokeWidth={stroke} />
        {/* progress arc */}
        <circle
          cx={cx} cy={cy} r={r} fill="none"
          stroke={color.arc} strokeWidth={stroke} strokeLinecap="butt"
          strokeDasharray={`${arcLen} ${circ}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
        {/* arrowhead at the tip */}
        <polygon
          points="-14,-14 18,0 -14,14"
          fill={color.arc}
          transform={`translate(${tipX} ${tipY}) rotate(${tangent})`}
        />
        {/* center status text */}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="15" fontWeight="700" fill={color.text} className="uppercase tracking-wider" data-testid="lifecycle-status">
          {m.label}
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fontWeight="600" fill="#475569" data-testid="lifecycle-progress">
          {Math.round(m.progress * 100)}% of lifecycle
        </text>
      </svg>
      <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide px-2.5 py-1 rounded-full border ${color.pill}`}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color.arc }} />
        {m.bucket}
      </div>
    </div>
  );
};

// ------- Sortable header -------
const SortHeader = ({ label, keyName, sortKey, sortDir, onSort, align = "left" }) => {
  const active = sortKey === keyName;
  const Icon = !active ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th
      className={`text-${align} p-3 cursor-pointer select-none hover:bg-gray-100 transition-colors`}
      onClick={() => onSort(keyName)}
      data-testid={`sort-${keyName}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "float-right" : ""} ${active ? "text-[var(--brand)]" : ""}`}>
        {label}
        <Icon size={12} className={active ? "opacity-100" : "opacity-40"} />
      </span>
    </th>
  );
};

// ------- Detail panel (rendered inline under an expanded PO row) -------
const LifecyclePanel = ({ po }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/purchase-orders/${po.id}/lifecycle`)
      .then(r => { if (mounted) setData(r.data); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [po.id]);

  if (loading) return <div className="p-10 text-center text-gray-400 text-sm">Loading lifecycle…</div>;
  if (!data) return <div className="p-10 text-center text-gray-400 text-sm">No lifecycle data.</div>;

  const c = data.counts || {};
  const product = data.product || {};

  return (
    <div className="bg-gradient-to-b from-[var(--bg-soft)] to-white px-6 py-6" data-testid={`lifecycle-panel-${po.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px_1fr] gap-6 items-start">

        {/* Left: PO & vendor details */}
        <div className="space-y-3">
          <p className="label-eyebrow">Purchase Order</p>
          <div className="bg-white border border-[var(--border)] rounded-lg p-4 space-y-2 text-sm">
            <Row k="PO #"><span className="font-mono text-[var(--brand)]">{po.po_number}</span></Row>
            <Row k="Placed">{po.created_at?.slice(0, 10)}</Row>
            <Row k="Vendor"><span className="capitalize">{po.vendor_business_name || "—"}</span></Row>
            <Row k="POC">
              {po.vendor_poc_name ? <span className="capitalize">{po.vendor_poc_name}</span> : "—"}
              {po.vendor_poc_email && <div className="text-xs text-gray-500 normal-case">{po.vendor_poc_email}</div>}
              {po.vendor_poc_phone && <div className="text-xs text-gray-500">{po.vendor_poc_phone}</div>}
            </Row>
            <Row k="Product"><span className="capitalize font-medium">{po.product_name}</span>{product.sku && <span className="text-xs text-gray-400 ml-2 font-mono">{product.sku}</span>}</Row>
            <Row k="Qty ordered"><span className="tabular-nums">{po.qty}</span></Row>
            <Row k="Payment terms">{po.payment_terms}</Row>
            {po.confirmed_at && <Row k="Confirmed">{po.confirmed_at?.slice(0, 10)}{po.confirmation_source ? ` · ${po.confirmation_source}` : ""}</Row>}
            {po.received_at && <Row k="Received">{po.received_at?.slice(0, 10)}{po.received_qty ? ` · ${po.received_qty} units` : ""}</Row>}
          </div>
        </div>

        {/* Center: Circular arrow */}
        <div className="flex justify-center">
          <CircularArrow status={po.status} />
        </div>

        {/* Right: Product journey stats */}
        <div className="space-y-3">
          <p className="label-eyebrow">Product Journey</p>
          <div className="grid grid-cols-2 gap-3">
            <StatTile icon={<Package size={14} />}   label="Received"       value={c.received}         color="text-blue-700" />
            <StatTile icon={<Warehouse size={14} />} label="Warehouse"      value={c.warehouse_on_hand} color="text-slate-700" />
            <StatTile icon={<Truck size={14} />}     label="Sent to DPs"    value={c.wh_to_ep}         color="text-amber-700" />
            <StatTile icon={<Truck size={14} />}     label="On DP vehicles" value={c.ep_on_hand}       color="text-purple-700" />
            <StatTile icon={<Users size={14} />}     label="Sold direct"    value={c.wh_to_client}     color="text-emerald-700" />
            <StatTile icon={<Users size={14} />}     label="Sold via DP"    value={c.ep_to_client}     color="text-emerald-700" />
          </div>

          {data.ep_breakdown?.length > 0 && (
            <div className="bg-white border border-[var(--border)] rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">On-hand by Distributing Partner</p>
              <ul className="space-y-1 text-sm">
                {data.ep_breakdown.map((e, i) => (
                  <li key={i} className="flex justify-between capitalize">
                    <span className="text-gray-600">{e.ep_name}</span>
                    <span className="tabular-nums font-semibold">{e.qty}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Recent movements ledger */}
      {data.recent_movements?.length > 0 && (
        <div className="mt-6 bg-white border border-[var(--border)] rounded-lg overflow-hidden">
          <p className="label-eyebrow px-4 pt-3">Recent movements</p>
          <table className="w-full text-xs mt-2">
            <thead className="bg-[var(--bg-soft)] text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Flow</th>
                <th className="text-right p-2">Qty</th>
                <th className="text-left p-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_movements.map(r => (
                <tr key={r.id} className="border-t border-[var(--border)]">
                  <td className="p-2 whitespace-nowrap">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px]">{r.type}</span></td>
                  <td className="p-2 text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      {r.source} <ArrowRight size={10} className="text-gray-400" /> {r.destination}
                    </span>
                  </td>
                  <td className="p-2 text-right tabular-nums font-semibold">{r.qty >= 0 ? "+" : ""}{r.qty}</td>
                  <td className="p-2 text-gray-500">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Row = ({ k, children }) => (
  <div className="flex justify-between gap-4 border-b last:border-b-0 border-[var(--border)] pb-1.5 last:pb-0">
    <dt className="text-xs uppercase tracking-wide text-gray-500 whitespace-nowrap">{k}</dt>
    <dd className="text-right">{children}</dd>
  </div>
);

const StatTile = ({ icon, label, value, color }) => (
  <div className="bg-white border border-[var(--border)] rounded-lg p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1">{icon}{label}</div>
    <div className={`text-xl font-bold tabular-nums ${color}`}>{Number(value || 0).toLocaleString()}</div>
  </div>
);

// ------- Main page -------
export default function SupplyChainPage() {
  const [pos, setPos] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [orderSortKey, setOrderSortKey] = useState("created_at");
  const [orderSortDir, setOrderSortDir] = useState("desc");

  useEffect(() => {
    api.get("/purchase-orders").then(r => setPos(r.data));
    api.get("/orders").then(r => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const toggleOrderSort = (key) => {
    if (orderSortKey === key) setOrderSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setOrderSortKey(key); setOrderSortDir("asc"); }
  };

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (p) => {
      switch (sortKey) {
        case "po_number":              return p.po_number || "";
        case "vendor_business_name":   return (p.vendor_business_name || "").toLowerCase();
        case "product_name":           return (p.product_name || "").toLowerCase();
        case "qty":                    return Number(p.qty) || 0;
        case "payment_terms":          return p.payment_terms || "";
        case "status":                 return p.status || "";
        case "received_qty":           return Number(p.received_qty) || 0;
        case "created_at":
        default:                       return p.created_at || "";
      }
    };
    return [...pos].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [pos, sortKey, sortDir]);

  const top10 = sorted.slice(0, 10);

  const sortedOrders = useMemo(() => {
    const dir = orderSortDir === "asc" ? 1 : -1;
    const getVal = (o) => {
      switch (orderSortKey) {
        case "client_name": return (o.client_name || "").toLowerCase();
        case "source":      return o.source || "";
        case "total":       return Number(o.total) || 0;
        case "status":      return (o.status || "").toLowerCase();
        case "items":       return (o.items?.length || o.products?.length || 0);
        case "fulfilled_by_name": return (o.fulfilled_by_name || "").toLowerCase();
        case "created_at":
        default:            return o.created_at || "";
      }
    };
    return [...orders].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [orders, orderSortKey, orderSortDir]);

  const topOrders = sortedOrders.slice(0, 10);

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="mb-6">
        <p className="label-eyebrow">Bird's-eye workflow · read-only</p>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Supply Chain</h1>
        <p className="text-gray-500 mt-1 text-sm">
          Every product's lifecycle from purchase order → warehouse → distributing partner → client. Click any row to expand.
        </p>
      </header>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs" data-testid="lifecycle-legend">
        <LegendPill color="#2563eb" label="Pending — ordered, confirmed, shipped" />
        <LegendPill color="#059669" label="Satisfied — received, fulfilled, sold, promo" />
        <LegendPill color="#dc2626" label="Trouble — RMA, lost, stolen, damaged, cancelled" />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="supply-chain-table">
            <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="p-3 w-10"><SelectAllCheckbox records={top10} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /></th>
                <SortHeader label="PO #"     keyName="po_number"            sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Date"     keyName="created_at"           sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Vendor"   keyName="vendor_business_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Product"  keyName="product_name"         sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Qty"      keyName="qty"                  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHeader label="Received" keyName="received_qty"         sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHeader label="Terms"    keyName="payment_terms"        sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Status"   keyName="status"               sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {top10.map(p => {
                const expanded = expandedId === p.id;
                const meta = metaFor(p.status);
                const c = BUCKET_COLOR[meta.bucket];
                return (
                  <React.Fragment key={p.id}>
                    <tr
                      className={`border-t border-[var(--border)] cursor-pointer hover:bg-gray-50 transition-colors ${expanded ? "bg-blue-50/40" : ""}`}
                      onClick={() => setExpandedId(expanded ? null : p.id)}
                      data-testid={`po-row-${p.id}`}
                    >
                      <td className="p-3 text-gray-400">
                        {expanded
                          ? <ChevronDown size={16} data-testid={`chevron-down-${p.id}`} />
                          : <ChevronRight size={16} data-testid={`chevron-right-${p.id}`} />}
                      </td>
                      <td className="p-3" onClick={(event) => event.stopPropagation()}><SelectionCheckbox checked={selectedIds.includes(p.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, p.id] : selectedIds.filter((id) => id !== p.id))} testId={`select-row-checkbox-${p.id}`} label={`Select ${p.po_number}`} /></td>
                      <td className="p-3 font-mono text-xs text-[var(--brand)]">{p.po_number}</td>
                      <td className="p-3">{p.created_at?.slice(0, 10)}</td>
                      <td className="p-3 capitalize">{p.vendor_business_name || "—"}</td>
                      <td className="p-3 capitalize">{p.product_name}</td>
                      <td className="p-3 text-right tabular-nums">{p.qty}</td>
                      <td className="p-3 text-right tabular-nums text-emerald-600">{p.received_qty || "—"}</td>
                      <td className="p-3">{p.payment_terms}</td>
                      <td className="p-3">
                        <span className={`status-pill inline-flex items-center gap-1 ${c.pill}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.arc }} />
                          {meta.label}
                        </span>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={9} className="p-0">
                          <LifecyclePanel po={p} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {top10.length === 0 && (
                <tr><td colSpan={9} className="p-10 text-center text-gray-400">No purchase orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {pos.length > 10 && (
          <div className="px-4 py-2 text-xs text-gray-500 border-t border-[var(--border)] bg-[var(--bg-soft)]">
            Showing 10 most recent of {pos.length} POs. See <Link to="/purchase-orders" className="text-[var(--brand)] underline">Purchase Orders</Link> for the full list.
          </div>
        )}
      </Card>
      <BulkActions section="purchase_orders" records={top10} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={() => api.get("/purchase-orders").then((r) => setPos(r.data))} />

      {/* ==================== Client Orders lifecycle ==================== */}
      <div className="mt-10 mb-4 flex items-baseline gap-3">
        <h2 className="text-2xl font-bold tracking-tight">Client Orders</h2>
        <p className="text-gray-500 text-sm">
          Direct Site · Client Portal · Wholesale · Admin — every sale, same lifecycle ring.
        </p>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="supply-chain-orders-table">
            <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3 w-8"></th>
                <SortHeader label="Date"     keyName="created_at"       sortKey={orderSortKey} sortDir={orderSortDir} onSort={toggleOrderSort} />
                <SortHeader label="Client"   keyName="client_name"      sortKey={orderSortKey} sortDir={orderSortDir} onSort={toggleOrderSort} />
                <SortHeader label="Source"   keyName="source"           sortKey={orderSortKey} sortDir={orderSortDir} onSort={toggleOrderSort} />
                <SortHeader label="Items"    keyName="items"            sortKey={orderSortKey} sortDir={orderSortDir} onSort={toggleOrderSort} align="right" />
                <SortHeader label="Total"    keyName="total"            sortKey={orderSortKey} sortDir={orderSortDir} onSort={toggleOrderSort} align="right" />
                <SortHeader label="Fulfiller" keyName="fulfilled_by_name" sortKey={orderSortKey} sortDir={orderSortDir} onSort={toggleOrderSort} />
                <SortHeader label="Status"   keyName="status"           sortKey={orderSortKey} sortDir={orderSortDir} onSort={toggleOrderSort} />
              </tr>
            </thead>
            <tbody>
              {topOrders.map(o => {
                const expanded = expandedOrderId === o.id;
                const meta = metaFor(o.status);
                const c = BUCKET_COLOR[meta.bucket];
                const items = o.items || o.products || [];
                const source = o.source || (o.payment_source === "stripe-cart" ? "direct_site" : (o.type === "wholesale" ? "wholesale" : "admin"));
                const sourceLabel = { direct_site: "Direct Site", client_portal: "Client Portal", wholesale: "Wholesale", admin: "Admin" }[source] || source;
                const sourcePill = {
                  direct_site: "bg-blue-100 text-blue-800 border-blue-200",
                  client_portal: "bg-purple-100 text-purple-800 border-purple-200",
                  wholesale: "bg-amber-100 text-amber-800 border-amber-200",
                  admin: "bg-gray-100 text-gray-700 border-gray-200",
                }[source] || "bg-gray-100 text-gray-700 border-gray-200";
                return (
                  <React.Fragment key={o.id}>
                    <tr
                      className={`border-t border-[var(--border)] cursor-pointer hover:bg-gray-50 transition-colors ${expanded ? "bg-blue-50/40" : ""}`}
                      onClick={() => setExpandedOrderId(expanded ? null : o.id)}
                      data-testid={`order-row-${o.id}`}
                    >
                      <td className="p-3 text-gray-400">
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </td>
                      <td className="p-3">{o.created_at?.slice(0, 10)}</td>
                      <td className="p-3 capitalize font-medium">{o.client_name || o.client_email || "—"}</td>
                      <td className="p-3">
                        <span className={`status-pill ${sourcePill}`}>{sourceLabel}</span>
                      </td>
                      <td className="p-3 text-right tabular-nums">{items.length}</td>
                      <td className="p-3 text-right tabular-nums">${Number(o.total || 0).toFixed(2)}{Number(o.tip_amount || o.tip?.amount || 0) > 0 && <div className="text-[10px] text-amber-700" data-testid={`supply-order-tip-${o.id}`}>+${Number(o.tip_amount || o.tip?.amount || 0).toFixed(2)} tip</div>}</td>
                      <td className="p-3 capitalize">{o.fulfilled_by_name || "—"}</td>
                      <td className="p-3">
                        <span className={`status-pill inline-flex items-center gap-1 ${c.pill}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.arc }} />
                          {meta.label}
                        </span>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <OrderLifecyclePanel order={o} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {topOrders.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-gray-400">No client orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {orders.length > 10 && (
          <div className="px-4 py-2 text-xs text-gray-500 border-t border-[var(--border)] bg-[var(--bg-soft)]">
            Showing 10 most recent of {orders.length} orders. See <Link to="/orders" className="text-[var(--brand)] underline">Client Orders</Link> for the full list.
          </div>
        )}
      </Card>
    </div>
  );
}

const LegendPill = ({ color, label }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[var(--border)]">
    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
    <span className="text-gray-600">{label}</span>
  </div>
);

// ------- Order lifecycle panel (mirror of LifecyclePanel, for Client Orders) -------
const OrderLifecyclePanel = ({ order }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/orders/${order.id}/lifecycle`)
      .then(r => { if (mounted) setData(r.data); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [order.id]);

  if (loading) return <div className="p-10 text-center text-gray-400 text-sm">Loading lifecycle…</div>;
  if (!data) return <div className="p-10 text-center text-gray-400 text-sm">No lifecycle data.</div>;

  const items = data.order?.items || data.order?.products || [];
  const c = data.counts || {};
  const source = order.source || (order.payment_source === "stripe-cart" ? "direct_site" : (order.type === "wholesale" ? "wholesale" : "admin"));
  const sourceLabel = { direct_site: "Direct Site", client_portal: "Client Portal", wholesale: "Wholesale", admin: "Admin" }[source] || source;

  return (
    <div className="bg-gradient-to-b from-[var(--bg-soft)] to-white px-6 py-6" data-testid={`order-lifecycle-panel-${order.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px_1fr] gap-6 items-start">
        {/* Left: order + client */}
        <div className="space-y-3">
          <p className="label-eyebrow">Order</p>
          <div className="bg-white border border-[var(--border)] rounded-lg p-4 space-y-2 text-sm">
            <Row k="Order #"><span className="font-mono text-[var(--brand)]">{order.id?.slice(0, 8)}</span></Row>
            <Row k="Placed">{order.created_at?.slice(0, 16).replace("T", " ")}</Row>
            <Row k="Client"><span className="capitalize">{order.client_name || "—"}</span>{order.client_email && <div className="text-xs text-gray-500 normal-case">{order.client_email}</div>}</Row>
            {data.client?.company && <Row k="Company"><span className="capitalize">{data.client.company}</span></Row>}
            <Row k="Source"><span className="capitalize">{sourceLabel}</span></Row>
            {order.stripe_session_id && <Row k="Stripe"><span className="text-xs font-mono">{order.stripe_session_id.slice(0, 14)}…</span></Row>}
            <Row k="Fulfiller">{order.fulfilled_by_name || "—"}</Row>
            {Number(order.tip_amount || order.tip?.amount || 0) > 0 && <Row k="Tip"><span data-testid={`supply-tip-attribution-${order.id}`}>${Number(order.tip_amount || order.tip?.amount || 0).toFixed(2)} · {order.tip_attributed_to_name || "Awaiting assignment"}</span></Row>}
          </div>
        </div>

        {/* Center: Circular arrow */}
        <div className="flex justify-center">
          <CircularArrow status={order.status} />
        </div>

        {/* Right: Line items */}
        <div className="space-y-3">
          <p className="label-eyebrow">Line items · {items.length}</p>
          <div className="bg-white border border-[var(--border)] rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-soft)] text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left p-2">Item</th>
                  <th className="text-right p-2">Qty</th>
                  <th className="text-right p-2">Line</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-t border-[var(--border)]">
                    <td className="p-2">
                      <div className="capitalize font-medium">{it.name}</div>
                      {it.sku && <div className="text-[10px] text-gray-500 font-mono">{it.sku}</div>}
                    </td>
                    <td className="p-2 text-right tabular-nums">{it.qty}</td>
                    <td className="p-2 text-right tabular-nums">${((it.unit_price || 0) * (it.qty || 0)).toFixed(2)}</td>
                  </tr>
                ))}
                {Number(order.tip_amount || order.tip?.amount || 0) > 0 && (
                  <tr className="border-t border-amber-200 bg-amber-50/60" data-testid={`supply-tip-line-${order.id}`}><td className="p-2 font-medium text-amber-900">{order.tip?.label || "Tip"}</td><td className="p-2 text-right">—</td><td className="p-2 text-right tabular-nums text-amber-900">${Number(order.tip_amount || order.tip?.amount || 0).toFixed(2)}</td></tr>
                )}
                <tr className="border-t-2 border-[var(--border)] font-semibold bg-[var(--bg-soft)]">
                  <td className="p-2">Total</td>
                  <td className="p-2 text-right tabular-nums">{c.total_units}</td>
                  <td className="p-2 text-right tabular-nums">${Number(order.total ?? c.line_total ?? 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent movements */}
      {data.recent_movements?.length > 0 && (
        <div className="mt-6 bg-white border border-[var(--border)] rounded-lg overflow-hidden">
          <p className="label-eyebrow px-4 pt-3">Recent movements for these SKUs</p>
          <table className="w-full text-xs mt-2">
            <thead className="bg-[var(--bg-soft)] text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Flow</th>
                <th className="text-right p-2">Qty</th>
                <th className="text-left p-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_movements.map(m => (
                <tr key={m.id} className="border-t border-[var(--border)]">
                  <td className="p-2 whitespace-nowrap">{m.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px]">{m.type}</span></td>
                  <td className="p-2 text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      {m.source} <ArrowRight size={10} className="text-gray-400" /> {m.destination}
                    </span>
                  </td>
                  <td className="p-2 text-right tabular-nums font-semibold">{m.qty >= 0 ? "+" : ""}{m.qty}</td>
                  <td className="p-2 text-gray-500">{m.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
